/**
 * Member Metric Card Component
 * 
 * Reusable component for displaying member analytics metrics
 * with icons, values, and optional trend indicators.
 */

import React from 'react';
import { clsx } from 'clsx';
import { formatMoney, BasisPointsService } from '@zlinkx/shared-v2';

interface MemberMetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  className?: string;
  valueClassName?: string;
  format?: 'currency' | 'number' | 'percent';
}

const MemberMetricCard: React.FC<MemberMetricCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  className,
  valueClassName,
  format = 'number'
}) => {
  // Format the value based on type
  const formatValue = (val: number | string): string => {
    if (typeof val === 'string') return val;
    
    switch (format) {
      case 'currency':
        return formatMoney(val);
      case 'percent':
        // Value is in basis points, use formatShort for cleaner display
        return BasisPointsService.formatShort(val);
      case 'number':
      default:
        return new Intl.NumberFormat('en-US').format(val);
    }
  };

  // Get trend icon and color
  const getTrendIcon = () => {
    if (trend === undefined) return null;

    switch (trend) {
      case 'up':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      case 'down':
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
          </svg>
        );
      case 'neutral':
        return (
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
          </svg>
        );
    }
  };

  return (
    <div className={clsx('bg-white p-4 rounded-lg border border-gray-200', className)}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            {icon !== undefined && icon !== null && <div className="text-gray-400">{icon}</div>}
            <p className="text-sm font-medium text-gray-600">{title}</p>
          </div>
          <div className="mt-2 flex items-baseline space-x-2">
            <p className={clsx('text-2xl font-semibold text-gray-900', valueClassName)}>
              {formatValue(value)}
            </p>
            {trend !== undefined && getTrendIcon()}
          </div>
          {subtitle && (
            <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberMetricCard;
